/**
 * Level 3's states, from reads the platform already publishes — owner ruling DEC-107,
 * `docs/adr/9046`.
 *
 * The level 3 grid (one store × article, every size of the scale) and the level 2 store table are
 * drawn before the network-health read is pinned, from `GET /inseason/stock-positions` (every
 * cell, `not_ranged` and `not_reported` included), the run's proposals and transfers, the open
 * alerts, the exception rules and the registry's effective values. The definitions are DEC-107's
 * and the sweep's own (bata-platform `planning.exception.observations`, ADR 2100/2105/2108), so a
 * size reads here as the platform's read will count it:
 *
 *   served      the cell is in the run's partition and is not `not_ranged` (DEC-107: no assortment
 *               source exists yet — the page says so)
 *   na (?)      not reported or not counted — and, for a stocked size, a stale partition, no or
 *               sub-floor demand, or a cover rule that cannot be applied — always with its reason
 *   broken (▥)  on hand 0 and the size-gap verdict (the sweep's `size_gap`: the units an interior
 *               gap strands in its stocked siblings, by declared run position) breaches the
 *               size-run rule's comparator and governed threshold
 *   out (✕)     on hand 0 and not broken (an edge, the whole article, a gap with no verdict)
 *   low (◔)     on hand > 0, weeks of cover below the cover rule `inseason.cover_below`
 *   ok (✓)      the rest
 *
 * **Precedence — DEC-108 as amended to the platform's statement** (#505): na (not reported / not
 * counted) › broken › out › na (stale, demand below floor, demand not forecast, no demand, cover
 * rule not applicable) › low › ok. «Rotture per taglia» = out + broken. After the re-pin level 3
 * reads each size's state from `…/network-health/sizes`; this derivation is the pre-pin fallback.
 *
 * **Cover per size.** The grid judges each size's own `weeks_of_cover` against the cover rule's
 * threshold, so the hole is in the right column; the store × article's cover (Σ committed ÷ Σ
 * demand, the rule's figure) is shown on level 2's row.
 *
 * **Covered or not** (DEC-108, DEC-109, owner decisions F1/F2 of 2026-10-05 on bata-ui#302): a
 * problem cell is covered ONLY by a non-rejected proposal line or a non-rejected inbound transfer
 * line of the run AT THE CELL. An open alert moves no pair, so it does not cover (F2): it is shown
 * beside the cell as «avviso aperto». A price step does not cover a stock-out either (F1): it is
 * shown beside it as «intervento di prezzo». Neither is ever counted as coverage here.
 *
 * Beside an uncovered stock-out the page also says what it can see of why: stock already on the
 * way (`committed_units` above `on_hand_units` — «merce in arrivo», {@link isArriving}) and a move
 * the weekly move cap deferred on the store × article («rinviato per tetto movimenti»).
 *
 * Every function here is pure.
 */

import type { AlertSummary, ProposalSummary, StockPositionSummary, TransferSummary } from '@/api/generated';
import { sizeAxis, sizeName, type SizeName } from '@/planning';

/**
 * One store-to-store line as level 2 and level 3 read it: a transfer row of a run recorded before the
 * move run, or a counted line of the tick's move run (`./reads.ts` `moveLineOf`, bata-platform ADR 6055
 * amended 2026-10-31) — one shape, so the cover and the composition are counted the same way for both.
 * `transfer_id` is the line's id (a move's `line_id`); `disposition` its newest decision.
 */
export type StoreMoveLine = Pick<TransferSummary, 'transfer_id' | 'donor_store_key' | 'recipient_store_key' | 'article_key' | 'size_key' | 'units'> & {
  readonly disposition?: string | null;
};

import type { HealthState } from '@/api/pending/network-health';

// ── the rules as the page could read them ────────────────────────────────────

/** Why a rule's test cannot be applied. `notRead`: the read behind it failed or is pending. */
export type CriterionAbsence = 'ruleNotDeclared' | 'comparatorNotKnown' | 'thresholdNotInForce' | 'notRead';

export interface Criterion {
  readonly ruleId: string;
  readonly comparator: string | null;
  readonly threshold: number | null;
  readonly absent: CriterionAbsence | null;
}

export interface Criteria {
  readonly cover: Criterion;
  readonly sizeGap: Criterion;
  /**
   * The cover rule's governed demand floor (ADR 2108): `null` when no row is in force — then
   * nothing is floored (the platform never invents one). `undefined` when it could not be read.
   */
  readonly demandFloor: number | null | undefined;
}

/** The sweep's comparators (bata-platform `services.jobs.foundations.COMPARATORS`), verbatim. */
const COMPARATORS: Readonly<Record<string, (observed: number, threshold: number) => boolean>> = {
  greater_than: (observed, threshold) => observed > threshold,
  greater_or_equal: (observed, threshold) => observed >= threshold,
  less_than: (observed, threshold) => observed < threshold,
  less_or_equal: (observed, threshold) => observed <= threshold,
  outside_band: (observed, threshold) => Math.abs(observed) > threshold,
};

export const COVER_RULE_ID = 'inseason.cover_below';
export const SIZE_RUN_RULE_ID = 'inseason.size_run_broken';
export const COVER_FLOOR_PARAMETER = 'exception.rule.inseason.cover_below.min_weekly_demand';

/** A rule's comparator and threshold, or why the test cannot be applied. */
export function criterionOf(
  ruleId: string,
  rule: { readonly comparator: string } | null | undefined,
  threshold: number | null | undefined,
): Criterion {
  if (rule === undefined || threshold === undefined) return { ruleId, comparator: null, threshold: null, absent: 'notRead' };
  if (rule === null) return { ruleId, comparator: null, threshold: null, absent: 'ruleNotDeclared' };
  if (COMPARATORS[rule.comparator] === undefined) return { ruleId, comparator: rule.comparator, threshold, absent: 'comparatorNotKnown' };
  if (threshold === null) return { ruleId, comparator: rule.comparator, threshold: null, absent: 'thresholdNotInForce' };
  return { ruleId, comparator: rule.comparator, threshold, absent: null };
}

/** Whether `observed` breaches the rule; `null` when the rule cannot be applied. */
export function breaches(criterion: Criterion, observed: number): boolean | null {
  if (criterion.absent !== null || criterion.comparator === null || criterion.threshold === null) return null;
  const test = COMPARATORS[criterion.comparator];
  return test === undefined ? null : test(observed, criterion.threshold);
}

// ── the run's lines at one store × article ───────────────────────────────────

const REJECTED = 'reject';
const notRejected = (line: { readonly disposition?: string | null }): boolean => line.disposition !== REJECTED;

export interface TransferLeg {
  /** The line's id: a transfer's, or a move line's (`line_id`). */
  readonly transferId: string;
  /** The other store: the donor of an inbound leg, the recipient of an outbound one. */
  readonly otherStore: string;
  readonly units: number;
  /** The line's newest decision (`null` undecided) — a decided move is no row of the pending moves list. */
  readonly disposition: string | null;
}

/** What the run does for one size of one store × article. */
export interface SizeInterventions {
  readonly proposalUnits: number;
  readonly proposalLines: number;
  readonly transfersIn: readonly TransferLeg[];
  readonly transfersOut: readonly TransferLeg[];
}

export const NO_INTERVENTION: SizeInterventions = { proposalUnits: 0, proposalLines: 0, transfersIn: [], transfersOut: [] };

/** The run's lines (not rejected) at `storeKey` × `articleKey`, by size key. */
export function interventionsBySize(
  storeKey: string,
  articleKey: string,
  proposals: readonly ProposalSummary[],
  transfers: readonly StoreMoveLine[],
): ReadonlyMap<string, SizeInterventions> {
  const bySize = new Map<string, { proposalUnits: number; proposalLines: number; transfersIn: TransferLeg[]; transfersOut: TransferLeg[] }>();
  const at = (sizeKey: string) => {
    const held = bySize.get(sizeKey);
    if (held !== undefined) return held;
    const fresh = { proposalUnits: 0, proposalLines: 0, transfersIn: [] as TransferLeg[], transfersOut: [] as TransferLeg[] };
    bySize.set(sizeKey, fresh);
    return fresh;
  };
  for (const line of proposals) {
    if (line.store_key !== storeKey || line.article_key !== articleKey || !notRejected(line)) continue;
    const cell = at(line.size_key);
    cell.proposalUnits += line.units;
    cell.proposalLines += 1;
  }
  for (const line of transfers) {
    if (line.article_key !== articleKey || !notRejected(line)) continue;
    if (line.recipient_store_key === storeKey) {
      at(line.size_key).transfersIn.push({ transferId: line.transfer_id, otherStore: line.donor_store_key, units: line.units, disposition: line.disposition ?? null });
    } else if (line.donor_store_key === storeKey) {
      at(line.size_key).transfersOut.push({ transferId: line.transfer_id, otherStore: line.recipient_store_key, units: line.units, disposition: line.disposition ?? null });
    }
  }
  return bySize;
}

/** Whether a size's lines cover a problem there: a proposal or an inbound transfer. */
const coversCell = (lines: SizeInterventions): boolean => lines.proposalLines > 0 || lines.transfersIn.length > 0;

/** The rules of the undecided alerts on one store × article (open or snoozed). */
export function openAlertRules(alerts: readonly AlertSummary[], storeKey: string, articleKey: string): readonly string[] {
  const rules = new Set<string>();
  for (const alert of alerts) {
    if (alert.storeKey !== storeKey || alert.articleKey !== articleKey) continue;
    if (alert.disposition !== null && alert.disposition !== undefined) continue;
    rules.add(alert.ruleId);
  }
  return [...rules].sort();
}

// ── one size ─────────────────────────────────────────────────────────────────

/** Why a size, or a store × article, is «Non valutabile» — `inseason.networkHealth.reason.*`. */
export type HealthReason =
  | 'notReported'
  | 'notCounted'
  | 'partitionStale'
  | 'noDemand'
  | 'demandNotForecast'
  | 'demandBelowFloor'
  | 'coverRuleAbsent'
  | 'notObserved'
  | 'other'
  | 'notServed';

/** Each reason's words — a literal table, so every key is a reference the catalogue check sees. */
export const REASON_KEYS: Readonly<Record<HealthReason, string>> = {
  notReported: 'inseason.networkHealth.reason.notReported',
  notCounted: 'inseason.networkHealth.reason.notCounted',
  partitionStale: 'inseason.networkHealth.reason.partitionStale',
  noDemand: 'inseason.networkHealth.reason.noDemand',
  demandNotForecast: 'inseason.networkHealth.reason.demandNotForecast',
  demandBelowFloor: 'inseason.networkHealth.reason.demandBelowFloor',
  coverRuleAbsent: 'inseason.networkHealth.reason.coverRuleAbsent',
  notObserved: 'inseason.networkHealth.reason.notObserved',
  other: 'inseason.networkHealth.reason.other',
  notServed: 'inseason.networkHealth.reason.notServed',
};

export interface SizeVerdict {
  readonly name: SizeName;
  readonly row: StockPositionSummary | null;
  /** DEC-107: in the partition and not `not_ranged`. */
  readonly served: boolean;
  /** `null` when the size is not served. */
  readonly state: HealthState | null;
  /** Why the size is not evaluable (or not served). */
  readonly reason: HealthReason | null;
  /** An out size whose broken-run verdict could not be given — said beside it, never guessed. */
  readonly gapUnknown: boolean;
  readonly interventions: SizeInterventions;
  /** A problem state with no intervention covering it; `null` when the state is not a problem. */
  readonly uncovered: boolean | null;
  /**
   * bata-ui#302 E: the platform's recorded reason the run wrote no line for the cell, with the
   * target and need behind it. Level 3 only (the sizes read); `null`/absent: not published.
   */
  readonly cause?: SizeCause;
}

/** The target, the need and the first cause that stopped a line (ADR 6057). Each `null` when not recorded. */
export interface SizeCause {
  readonly reason: string | null;
  readonly targetUnits: number | null;
  readonly needUnits: number | null;
}

const REPORTED = 'reported';
const NOT_RANGED = 'not_ranged';

const isServed = (row: StockPositionSummary | null): row is StockPositionSummary => row !== null && row.position_state !== NOT_RANGED;
const isCounted = (row: StockPositionSummary): boolean => row.position_state === REPORTED && row.on_hand_units !== null;

/**
 * The units an interior gap at `cell` strands in its siblings, or `null` when the verdict cannot
 * be given — the sweep's `_size_gap` (ADR 2105): nought for a stocked size, for an edge gap and for
 * a whole article out; the siblings' stock for an empty size with a stocked sibling on either side
 * by the declared run position; unknown on a stale partition, an uncounted sibling or an unplaced
 * size.
 */
export function strandedUnits(cell: StockPositionSummary, siblings: readonly StockPositionSummary[]): number | null {
  if (!cell.partition_is_fresh) return null;
  if (!isCounted(cell)) return null;
  if ((cell.on_hand_units ?? 0) > 0) return 0;
  if (!siblings.every((sibling) => isCounted(sibling) && sibling.partition_is_fresh)) return null;
  const stocked = siblings.filter((sibling) => (sibling.on_hand_units ?? 0) > 0);
  if (stocked.length === 0) return 0;
  const rank = cell.size_run_position;
  if (rank === null || rank === undefined) return null;
  const placed = stocked.flatMap((sibling) => (sibling.size_run_position === null || sibling.size_run_position === undefined ? [] : [sibling.size_run_position]));
  if (placed.some((other) => other < rank) && placed.some((other) => other > rank)) {
    return stocked.reduce((sum, sibling) => sum + (sibling.on_hand_units ?? 0), 0);
  }
  if (placed.length < stocked.length) return null;
  return 0;
}

/** The store × article's demand, when the floor applies: `true` when floored. */
function belowFloor(rows: readonly StockPositionSummary[], floor: number | null | undefined): boolean | null {
  if (floor === undefined) return null;
  if (floor === null) return false;
  if (rows.some((row) => row.weekly_demand === null)) return null;
  const demand = rows.reduce((sum, row) => sum + (row.weekly_demand ?? 0), 0);
  return demand > 0 && demand < floor;
}

/** One size's state, from its row and its siblings' (the served rows of the same store × article). */
export function sizeVerdict(
  name: SizeName,
  row: StockPositionSummary | null,
  served: readonly StockPositionSummary[],
  criteria: Criteria,
  lines: SizeInterventions,
): SizeVerdict {
  const base = { name, row, interventions: lines, gapUnknown: false };
  if (!isServed(row)) return { ...base, served: false, state: null, reason: 'notServed', uncovered: null };
  const counted = (reason: HealthReason): SizeVerdict => ({ ...base, served: true, state: 'na', reason, uncovered: null });
  // DEC-108 as the platform's statement applies it (bata-platform #505): na for want of a count ›
  // broken › out › na for want of a cover (stale, demand, rule) › low › ok.
  if (row.position_state !== REPORTED) return counted('notReported');
  if (row.on_hand_units === null) return counted('notCounted');
  // F2 (owner, 2026-10-05): only a line that moves pairs INTO the cell covers it. An open alert on
  // the store × article is shown beside it (`StoreArticleVerdict.openAlertRules`), never counted.
  const covered = coversCell(lines);

  if (row.on_hand_units === 0) {
    // Broken = the size-gap verdict breaches the size-run RULE (comparator and governed threshold),
    // the sweep's own measure (`size_gap_stranded_units`). No verdict — a stale partition, an
    // uncounted sibling, an unplaced size, or a rule that cannot be applied — is not broken: out.
    const siblings = served.filter((other) => other.size_key !== row.size_key);
    const stranded = strandedUnits(row, siblings);
    const verdict = stranded === null ? null : breaches(criteria.sizeGap, stranded);
    const state: HealthState = verdict === true ? 'broken' : 'out';
    return { ...base, served: true, state, reason: null, gapUnknown: verdict === null, uncovered: !covered };
  }

  if (!row.partition_is_fresh) return counted('partitionStale');
  const floored = belowFloor(served, criteria.demandFloor);
  if (floored === true) return counted('demandBelowFloor');
  if (row.weeks_of_cover === null) return counted(row.weekly_demand === null ? 'demandNotForecast' : 'noDemand');
  const low = breaches(criteria.cover, row.weeks_of_cover);
  if (low === null || floored === null) return counted('coverRuleAbsent');
  if (low) return { ...base, served: true, state: 'low', reason: null, uncovered: !covered };
  return { ...base, served: true, state: 'ok', reason: null, uncovered: null };
}

// ── one store × article ──────────────────────────────────────────────────────

export interface StoreArticleVerdict {
  readonly storeKey: string;
  readonly articleKey: string;
  /** Every size of the scale, in run order — the sizes this store does not hold included. */
  readonly sizes: readonly SizeVerdict[];
  /** The store × article's state (level 2's row): DEC-108's precedence over its cells, na › broken › out › low › ok. */
  readonly state: HealthState;
  readonly reason: HealthReason | null;
  /** Σ committed ÷ Σ weekly demand over the served sizes — the cover rule's figure — or `null`. */
  readonly cover: number | null;
  /** Units on hand over the served sizes; `null` when one is not counted. */
  readonly onHand: number | null;
  readonly uncovered: boolean | null;
  readonly openAlertRules: readonly string[];
  readonly lines: SizeInterventions;
}

/** The rule's cover over the served sizes, or why there is none (the sweep's `article_cover`). */
export function storeArticleCover(
  served: readonly StockPositionSummary[],
  floor: number | null | undefined,
): { readonly cover: number | null; readonly reason: HealthReason | null } {
  if (served.length === 0) return { cover: null, reason: 'notServed' };
  if (!served.every((row) => row.partition_is_fresh)) return { cover: null, reason: 'partitionStale' };
  if (served.some((row) => row.position_state !== REPORTED || row.committed_units === null)) return { cover: null, reason: 'notReported' };
  if (served.some((row) => row.weekly_demand === null)) return { cover: null, reason: 'demandNotForecast' };
  const demand = served.reduce((sum, row) => sum + (row.weekly_demand ?? 0), 0);
  if (demand <= 0) return { cover: null, reason: 'noDemand' };
  if (floor === undefined) return { cover: null, reason: 'coverRuleAbsent' };
  if (floor !== null && demand < floor) return { cover: null, reason: 'demandBelowFloor' };
  const position = served.reduce((sum, row) => sum + (row.committed_units ?? 0), 0);
  return { cover: position / demand, reason: null };
}

/**
 * One store × article: every size of `scale` (the article's sizes in the run, at any store), its
 * state, and the store × article's own. Pure.
 */
export function storeArticleVerdict(input: {
  readonly storeKey: string;
  readonly articleKey: string;
  /** Every stock row of the ARTICLE in the run (any store) — the scale is read from them. */
  readonly articleRows: readonly StockPositionSummary[];
  readonly proposals: readonly ProposalSummary[];
  /** The run's store-to-store lines — its transfer rows, or the tick's move lines (`StoreMoveLine`). */
  readonly transfers: readonly StoreMoveLine[];
  readonly alerts: readonly AlertSummary[];
  readonly criteria: Criteria;
}): StoreArticleVerdict {
  const { storeKey, articleKey, criteria } = input;
  const scale = sizeAxis(input.articleRows.filter((row) => row.article_key === articleKey).map((row) => sizeName(row)));
  const here = input.articleRows.filter((row) => row.store_key === storeKey && row.article_key === articleKey);
  const byKey = new Map(here.map((row) => [row.size_key, row]));
  const served = here.filter((row) => row.position_state !== NOT_RANGED);
  const lines = interventionsBySize(storeKey, articleKey, input.proposals, input.transfers);
  const rules = openAlertRules(input.alerts, storeKey, articleKey);
  const sizes = scale.map((name) => sizeVerdict(name, byKey.get(name.key) ?? null, served, criteria, lines.get(name.key) ?? NO_INTERVENTION));
  const { cover, reason: coverReason } = storeArticleCover(served, criteria.demandFloor);
  const onHand = served.length === 0 || served.some((row) => row.on_hand_units === null)
    ? null
    : served.reduce((sum, row) => sum + (row.on_hand_units ?? 0), 0);

  const total = sumLines([...lines.values()]);
  return { storeKey, articleKey, sizes, ...rowState(sizes, coverReason), cover, onHand, openAlertRules: rules, lines: total };
}

/**
 * The row's state over its sizes, in the platform's order by tier: a size not evaluable for want
 * of a COUNT outranks everything; broken; out; a size not evaluable for want of a COVER; low; ok —
 * so level 2 says what the cells say, never a third rule. Pure.
 */
export function rowState(
  sizes: readonly SizeVerdict[],
  coverReason: HealthReason | null = null,
): Pick<StoreArticleVerdict, 'state' | 'reason' | 'uncovered'> {
  const servedSizes = sizes.filter((size) => size.served);
  if (servedSizes.length === 0) return { state: 'na', reason: 'notServed', uncovered: null };
  const tierOf = (size: SizeVerdict): number => {
    if (size.state === 'na') return COUNT_REASONS.has(size.reason ?? '') ? 0 : 3;
    return ({ broken: 1, out: 2, low: 4, ok: 5 } as const)[size.state ?? 'ok'];
  };
  const top = Math.min(...servedSizes.map(tierOf));
  const inState = servedSizes.filter((size) => tierOf(size) === top);
  const state: HealthState = inState[0]?.state ?? 'ok';
  const reason = state === 'na' ? inState.find((size) => size.reason !== null)?.reason ?? coverReason : null;
  const uncovered = state === 'na' || state === 'ok' ? null : inState.some((size) => size.uncovered === true);
  return { state, reason, uncovered };
}

/** A size's state as the platform's sizes read states it (`healthState`, `healthReason`, `isUncovered`). */
export interface PlatformSizeState {
  readonly served: boolean;
  readonly state: HealthState | null;
  readonly reason: HealthReason | null;
  readonly uncovered: boolean | null;
  readonly cause?: SizeCause;
}

/**
 * After the re-pin: each size's state, reason and coverage are the platform's — the same statement
 * as the totals, so level 0 and level 3 cannot disagree. The figures and the lines stay the pinned
 * reads'. A size the platform does not list keeps the local verdict. Pure.
 */
export function withPlatformStates(verdict: StoreArticleVerdict, states: ReadonlyMap<string, PlatformSizeState>): StoreArticleVerdict {
  const sizes = verdict.sizes.map((size) => {
    const platform = states.get(size.name.key);
    return platform === undefined ? size : {
      ...size, served: platform.served, state: platform.state, reason: platform.reason, uncovered: platform.uncovered, gapUnknown: false,
      ...(platform.cause === undefined ? {} : { cause: platform.cause }),
    };
  });
  return { ...verdict, sizes, ...rowState(sizes) };
}

/** The platform's reason codes, as this build's reasons — an unknown code is `other`, never guessed. */
const PLATFORM_REASONS: Readonly<Record<string, HealthReason>> = {
  position_not_reported: 'notReported',
  on_hand_not_counted: 'notCounted',
  partition_stale: 'partitionStale',
  demand_below_floor: 'demandBelowFloor',
  demand_not_forecast: 'demandNotForecast',
  no_demand: 'noDemand',
  cover_rule_not_applicable: 'coverRuleAbsent',
  rule_not_declared: 'coverRuleAbsent',
  comparator_not_known: 'coverRuleAbsent',
  threshold_not_in_force: 'coverRuleAbsent',
  not_observed_for_run: 'notObserved',
  not_in_partition: 'notServed',
};

export function platformReason(code: string | null): HealthReason | null {
  if (code === null) return null;
  return PLATFORM_REASONS[code] ?? 'other';
}

/** The reasons that make a size not evaluable BEFORE broken and out — a position not counted. */
const COUNT_REASONS: ReadonlySet<string> = new Set<HealthReason>(['notReported', 'notCounted']);

function sumLines(all: readonly SizeInterventions[]): SizeInterventions {
  return {
    proposalUnits: all.reduce((sum, one) => sum + one.proposalUnits, 0),
    proposalLines: all.reduce((sum, one) => sum + one.proposalLines, 0),
    transfersIn: all.flatMap((one) => one.transfersIn),
    transfersOut: all.flatMap((one) => one.transfersOut),
  };
}

/**
 * «Merce in arrivo» (bata-ui#302 C): an uncovered stock-out whose position already counts stock on
 * the way — `committed_units` (on hand + in transit) above `on_hand_units`. The stock-out is still a
 * stock-out (in transit does not prevent one, DEC-107), and the pull's need is net of what is on the
 * way, which is often why no line was written. Pure.
 */
export function isArriving(size: Pick<SizeVerdict, 'state' | 'uncovered' | 'row'>): boolean {
  if (size.uncovered !== true || (size.state !== 'out' && size.state !== 'broken')) return false;
  const committed = size.row?.committed_units ?? null;
  const onHand = size.row?.on_hand_units ?? null;
  return committed !== null && onHand !== null && committed > onHand;
}

/** Pairs on the way to an uncovered stock-out (committed − on hand), or `null`. Pure. */
export function arrivingUnits(size: Pick<SizeVerdict, 'state' | 'uncovered' | 'row'>): number | null {
  return isArriving(size) ? (size.row?.committed_units ?? 0) - (size.row?.on_hand_units ?? 0) : null;
}

/** The address of a store × article in a set of keys — the move-cap deferrals' grain. */
export const storeArticleKey = (storeKey: string, articleKey: string): string => `${storeKey}|${articleKey}`;

/**
 * The store × articles the weekly move cap deferred a move FOR (the store that received less,
 * either stage), from the run's deferrals (`GET /inseason/runs/{run_id}/move-cap-deferrals`). Pure.
 */
export function deferredStoreArticles(deferrals: readonly { readonly store_key: string; readonly article_key: string }[]): ReadonlySet<string> {
  return new Set(deferrals.map((deferral) => storeArticleKey(deferral.store_key, deferral.article_key)));
}

/** The ORDER of level 2's rows — the brief's gravity, out › broken › low › ok › na (§4); the STATE is DEC-108's. */
export const STATE_GRAVITY: Readonly<Record<HealthState, number>> = { out: 0, broken: 1, low: 2, ok: 3, na: 4 };

/**
 * Level 2's store order (brief part 4 §4): the uncovered first, then by gravity, then by cover
 * ascending (an absent cover last), then by store key. Pure.
 */
export function compareStoreRows(a: StoreArticleVerdict, b: StoreArticleVerdict): number {
  const unc = (row: StoreArticleVerdict): number => (row.uncovered === true ? 0 : 1);
  return unc(a) - unc(b)
    || STATE_GRAVITY[a.state] - STATE_GRAVITY[b.state]
    || (a.cover ?? Number.POSITIVE_INFINITY) - (b.cover ?? Number.POSITIVE_INFINITY)
    || a.storeKey.localeCompare(b.storeKey)
    || a.articleKey.localeCompare(b.articleKey);
}
